import React from 'react';
import { Modal, ROLE } from 'baseui/modal';
import { KIND } from 'baseui/notification';
import { FormProvider } from 'react-hook-form';
import { FinalButton } from '../../../../Components/FinalButton';
import { MantraModalBodyOld } from '../../../../Components/Modal';
import { CenteredNotification } from '../../../../Components/Notification';
import { SupportLink } from '../../../../Components/SupportLink';
import { Text } from '../../../../globalStyles';
import { borderRadius } from '../../../../utils';
import { Modal as MantraModal } from '../../styles';
import { EditPatientCareFlowModalProps } from './types';
import { PatientCareFlowSection } from './PatientCareFlowSection';
import { usePatientCareFlowModal } from './hooks/logic/usePatientCareFlowModal';

const MODAL_OVERRIDES = {
  Dialog: {
    style: {
      ...borderRadius('4px'),
      paddingLeft: '4rem',
      paddingRight: '4rem',
      paddingTop: '2rem',
      paddingBottom: '2rem',
    },
  },
};

export const EditPatientCareFlowModal = ({
  isOpen,
  onClose,
}: EditPatientCareFlowModalProps): JSX.Element => {
  const {
    data,
    error,
    form,
    saving,
    visibleCareTypes,
    providers,
    sessionLimitsMap,
    disableSubmit,
    onSubmit,
    validateCareTypes,
  } = usePatientCareFlowModal({ isOpen, onClose });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeable
      animate
      size="auto"
      role={ROLE.dialog}
      overrides={MODAL_OVERRIDES}
      unstable_ModalBackdropScroll
    >
      <MantraModalBodyOld style={{ width: '325px' }}>
        <div className="mb4 pt2 flex-1">
          <Text.h2 className="mb2">Edit Care Types</Text.h2>
          <Text.body>
            To activate a care type for this patient, update a care type status from 'N/A' to
            'Active'.
          </Text.body>
        </div>
        <FormProvider {...form}>
          <form onSubmit={onSubmit}>
            <div className="flex flex-column gap-3 mb3">
              {visibleCareTypes.map(({ careType, providerTitle }) => (
                <PatientCareFlowSection
                  key={careType}
                  careType={careType}
                  providerTitle={providerTitle}
                  providers={providers}
                  hasSessionLimit={!!sessionLimitsMap[careType]}
                  validateCareTypes={validateCareTypes}
                  providersBlockedReason={data?.providersForUser.blockedReason}
                />
              ))}
            </div>
            {/* Persistent live region (always rendered, conditionally populated): screen
                readers announce the error reliably when its content changes — more robust
                than mounting/unmounting the alert node (WCAG 4.1.3). */}
            <div role="alert" aria-live="assertive" aria-atomic="true">
              {error && (
                <div className="mb3">
                  <CenteredNotification kind={KIND.negative}>
                    <div style={{ textAlign: 'center' }}>
                      {error.message}
                      {error.showContactLink && (
                        <>
                          <SupportLink />.
                        </>
                      )}
                    </div>
                  </CenteredNotification>
                </div>
              )}
            </div>
            <FinalButton
              className="w-100 mb3"
              loading={saving}
              kind="primary"
              type="submit"
              disabled={disableSubmit}
            >
              Save
            </FinalButton>
            <MantraModal.closeLink className="b" onClick={onClose}>
              Cancel
            </MantraModal.closeLink>
          </form>
        </FormProvider>
      </MantraModalBodyOld>
    </Modal>
  );
};
